'use client';

import { ExchangeStatus } from '@drift-labs/sdk';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import SemanticChip, {
	SEMANTIC_STATUS,
} from 'src/components/Utils/SemanticChip';
import useDriftClient from 'src/hooks/useDriftClient';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useIsMainnet from 'src/hooks/useIsMainnet';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDevSwitchIsOn from '../../hooks/useDevSwitchIsOn';
import React from 'react';
import constants from 'src/constants/autogenerated.json';
import { cj, cm } from 'src/utils/classNames';
import CurrentRpcHealth from '../CurrentRpcHealth';
import { SecretDevModeToggleButton } from '../SecretButton/SecretDevModeToggleButton';
import { WithChildren, WithClassName } from 'src/utils/UtilityTypes';
import Divider from 'src/components/primitives/Divider/Divider';
import { useNetworkInfoStore } from 'src/stores/useNetworkInfoStore';
import FeatureAnnouncementTrigger from 'src/components/FeatureAnnouncementTrigger/FeatureAnnouncementTrigger';

const statusToNum = (status: SEMANTIC_STATUS) => {
	switch (status) {
		case 'red':
			return 0;
		case 'amber':
			return 1;
		case 'green':
		default:
			return 2;
	}
};

const numToStatus = (status: number): SEMANTIC_STATUS => {
	switch (status) {
		case 0:
			return 'red';
		case 1:
			return 'amber';
		case 2:
		default:
			return 'green';
	}
};

const getMinimumHealth = (
	healthOne: SEMANTIC_STATUS,
	healthTwo: SEMANTIC_STATUS
) => {
	const minHealth = Math.min(statusToNum(healthOne), statusToNum(healthTwo));

	return numToStatus(minHealth);
};

const exchangeStatusToSemanticStatus = (
	exchangeStatus: number
): SEMANTIC_STATUS => {
	switch (exchangeStatus) {
		case ExchangeStatus.ACTIVE:
			return 'green';
		case ExchangeStatus.PAUSED:
			return 'red';
		default:
			return 'amber';
	}
};

const exchangeStatusToMessage = (exchangeStatus: number): string => {
	switch (exchangeStatus) {
		case ExchangeStatus.ACTIVE:
			return `Exchange Status: Active`;
		case ExchangeStatus.DEPOSIT_PAUSED:
			return `Exchange Status: Deposits Paused`;
		case ExchangeStatus.WITHDRAW_PAUSED:
			return `Exchange Status: Withdraws Paused`;
		case ExchangeStatus.AMM_PAUSED:
			return `Exchange Status: AMM Paused`;
		case ExchangeStatus.FILL_PAUSED:
			return `Exchange Status: Fills Paused`;
		case ExchangeStatus.LIQ_PAUSED:
			return `Exchange Status: Liquidations Paused`;
		case ExchangeStatus.FUNDING_PAUSED:
			return `Exchange Status: Funding Paused`;
		case ExchangeStatus.SETTLE_PNL_PAUSED:
			return `Exchange Status: PNL Settling Paused`;
		case ExchangeStatus.PAUSED:
			return `Exchange Status: Paused`;
		default:
			return `Exchange Status: Unknown`;
	}
};

const getOverralStatusMessage = (
	exchangeStatusMessageBlockchain: string,
	exchangeStatusMessageAws: string
) => {
	const messageParts: string[] = [];
	let overallStatusMessage = '';

	if (exchangeStatusMessageBlockchain) {
		messageParts.push(exchangeStatusMessageBlockchain);
	}

	if (exchangeStatusMessageAws) {
		messageParts.push(exchangeStatusMessageAws);
	}

	overallStatusMessage = messageParts.join('. ');

	if (!overallStatusMessage) {
		overallStatusMessage = '-';
	}

	return overallStatusMessage;
};

const Footer = (props: { hideStatus?: boolean }) => {
	const driftClientIsReady = useDriftClientIsReady();
	const driftClient = useDriftClient();

	const { status: exchangeStatusAws, message: statusMessageAws } =
		useDriftStore((s) => s.driftStatus.mainStatus);

	const exchangeStatusBlockchain =
		driftClientIsReady && driftClient?.getStateAccount()
			? (driftClient.getStateAccount().exchangeStatus ?? ExchangeStatus.ACTIVE)
			: ExchangeStatus.ACTIVE;

	const exchangeStatusBlockchainSemantic = exchangeStatusToSemanticStatus(
		exchangeStatusBlockchain
	);

	const overallStatus = getMinimumHealth(
		exchangeStatusAws,
		exchangeStatusBlockchainSemantic
	);

	const overallStatusMessage = getOverralStatusMessage(
		exchangeStatusToMessage(exchangeStatusBlockchain),
		statusMessageAws
	);

	const isMainnet = useIsMainnet();

	const devSwitchIsOn = useDevSwitchIsOn();
	const useMaxLev = useDriftStore((s) => s.tradeForm.maxLeverageSelected);
	const effectiveType = useNetworkInfoStore((s) => s.effectiveType);

	return (
		<footer
			id="footer"
			className={cj(
				'hidden w-full items-center justify-between px-lg py-xs sm:flex',
				'bg-container-bg typo-b4 text-text-secondary',
				// The footer is the same color as the majority of containers, so they blend
				// together when overlapping. This adds a visual separation to prevent the blend.
				'mt-xs'
			)}
		>
			{props.hideStatus || exchangeStatusBlockchain === undefined ? (
				<span />
			) : (
				<FooterSection>
					<Tooltip decoration="cursor-help" content={overallStatusMessage}>
						<SecretDevModeToggleButton>
							<div className="flex items-center gap-x-xs">
								<SemanticChip status={overallStatus} />
								<span>
									{overallStatus === 'green'
										? 'Online'
										: overallStatus === 'amber'
											? 'Some interruptions'
											: 'Major interruptions'}
								</span>
							</div>
						</SecretDevModeToggleButton>
					</Tooltip>

					<Divider vertical />

					<CurrentRpcHealth />

					<Divider vertical />

					<FeatureAnnouncementTrigger />
				</FooterSection>
			)}

			{devSwitchIsOn && useMaxLev && (
				<span className="text-text-negative-red-button">MAX LEV</span>
			)}

			<FooterSection className="hidden sm:flex">
				{(devSwitchIsOn || !isMainnet) && (
					<>
						<span>{isMainnet ? 'mainnet-beta' : 'devnet'}</span>
						<Divider vertical />
					</>
				)}
				{devSwitchIsOn && (
					<>
						<span>Network Effective Type: {effectiveType ?? 'Unknown'}</span>
						<Divider vertical />
					</>
				)}

				<span>v{constants.UI_VERSION}</span>
			</FooterSection>
		</footer>
	);
};

const FooterSection = ({
	className,
	children,
}: WithClassName<WithChildren>) => {
	return (
		<div className={cm('flex h-full items-center gap-x-lg', className)}>
			{children}
		</div>
	);
};

export default Footer;
